import { useEffect, useState } from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaListUl,
  FaHistory,
  FaCamera,
  FaClipboardList,
  FaCalculator,
  FaFileInvoiceDollar,
  FaPaperclip,
  FaPencilAlt,
} from "react-icons/fa";
import Hint from "../shared/Hint.jsx";
import { updateClaim } from "../../services/apiClient.js";
import { formatDate } from "../../utils/date.js";
import { statusName } from "../../utils/status.js";
import "../../styles/jobList.css";

// THE uniform job list — the one way a list of jobs renders anywhere in the app
// (search page, Workflow Management, RDR sections, future reports). Same Go-to
// quick-jump icons, same badges, same truncation, so users read every report the
// same way.
//
// COLUMNS ARE DATA (Robert 2026-08-03). Workflow Management wants a list that is
// genuinely different — an RDR Note of its own, the status and the date it was
// reached in one column, and no Division or Project Co — while every other
// section keeps the standard set. That is a `columns` array, NOT a second
// component: a fork would mean maintaining the badge engine, the Go-to rail, the
// filter chip, the click targets and the truncation rules twice, and they would
// drift. Pick columns and order here; everything else is inherited.

// Icon order mirrors the workspace tab rail (Karen 2026-07-25 order).
//
// COLOURS: six themed hues plus `purple`, which is NOT a Bootstrap variant —
// it is drawn from --bs-purple in jobList.css. Bootswatch sets $secondary and
// $light to shades of the page rather than to colours (Zephyr's $secondary is
// literally #fff), so a seventh button on either of them is white on white, and
// --bs-emphasis-color, the readable alternative, is black — which put Files next
// to the already-dark Quotes button and made the two hard to tell apart. Seven
// tabs needs a seventh HUE. See jobList.css before adding an eighth.
//
// The Inspection glyph is a CAMERA, not a hard hat (Robert 2026-08-07: at rail
// size the hard hat "looks like a bell"). It also matches the camera the user
// menu already uses for the inspector workspace, so the association is one the
// app has taught once rather than twice.
const TAB_ICONS = [
  { tab: "status", title: "Status & History", icon: FaHistory, color: "info" },
  { tab: "details", title: "Details", icon: FaListUl, color: "primary" },
  { tab: "inspection", title: "Inspection", icon: FaCamera, color: "warning" },
  { tab: "quotes", title: "Quotes", icon: FaCalculator, color: "dark" },
  { tab: "files", title: "Files", icon: FaPaperclip, color: "purple" },
  { tab: "progress", title: "Progress Notes", icon: FaClipboardList, color: "success" },
  { tab: "invoicing", title: "Invoicing", icon: FaFileInvoiceDollar, color: "danger" },
];

// The standard set — every section except Workflow Management.
export const DEFAULT_COLUMNS = [
  "claim", "client", "address", "status", "division", "jobType", "pco", "attention", "created", "goto",
];

// Workflow Management (Robert 2026-08-03, from 1.0's WFM list): the RDR note
// comes out from under the client name into a column of its own and is editable
// in place; Status absorbs the date beneath it ("Accepted (31-07-2026)"); and
// Division, Project Co and Created are dropped to pay for the room. Order
// follows 1.0 — the note sits beside the client, where the office reads it.
export const WFM_COLUMNS = [
  "claim", "client", "note", "address", "statusSince",
  // Job Type / Insurer / Assessor, added 2026-08-17 (Karen — "this is what 1.0
  // has"). They cost 380px, paid for partly by dropping the attention floor
  // below; see WFM_ATTENTION_FLOOR.
  "jobType", "insurer", "assessor",
  "attention", "goto",
];

// ---------------------------------------------------------------------------
// COLUMN WIDTHS, in px, used only when `aligned` is on (stacked lists — see
// styles/jobList.css for why the layout is fixed at all). They live here rather
// than in the CSS because the column SET now varies: the table's minimum width
// is the sum of whichever columns are showing, and a hand-maintained total in a
// stylesheet would quietly be wrong the first time a section dropped one.
//
// Two things they have to survive, which is why they are not cut to the bone:
//   1. html { font-size: 85% } (app.css) — the base is 13.6px, so text is
//      narrower than a 16px eyeballing suggests. Measured at 13.6px Inter.
//   2. THEMES CHANGE THE FONT. The Bootswatch themes each bring their own
//      family, so the same nine characters are wider in some than in Inter.
//      Roughly 10% headroom sits on the content-fit columns; if a theme clips a
//      header, that is the number to raise.
//
// Content-fit, measured against the dev database 2026-08-03:
//   claim        "Claim #" — the HEADER is the constraint, numbers are 5 digits
//   status       longest of six — "Requested" / "Inspected" / "Completed"
//   statusSince  the same plus " (31-07-2026)". The dates render with TABULAR
//                figures (.jl-num), which is what makes this number stable:
//                Inter's digits are proportional, so "15-07-2026" is measurably
//                narrower than "30-07-2026" and a width that fitted one date
//                clipped the next — the same column looking arbitrary row to
//                row. Tabular figures cost a couple of px and buy one worst
//                case for every date, plus dates that line up down the column.
//   division     longest of three — "Small Jobs" / "Streamline"
//   pco          longest name in use — "Mia-Mae Van Der Merwe"
//   created      one date format, dd-mm-yyyy
//   goto         200px, verified in Chrome and Firefox after the seventh button
//                clipped at 162px (2026-08-17). Compact 24px targets with 2px
//                gaps fit this existing column in every theme. Keep all seven
//                shortcuts in one row; Client and Address must keep their room.
//
// A CHOICE about how much to show, not a measurement — raising these costs
// table width, lowering them costs an earlier ellipsis:
//   client · address · contractor · note
//
// `attention` has NO width on purpose: under fixed layout the leftover goes to
// the columns without one, so it holds ATTENTION_FLOOR at the minimum and grows
// on a wide monitor. It is where the badges wrap, so it is the one that wants
// the room.
const WIDTHS = {
  claim: 64,
  // 170 until 2026-08-17. Measured: median 16 chars, 75th 23, and 150 holds ~23 —
  // so this still covers three quarters of real names and is the ONLY column on
  // Workflow Management with slack. Address (median 40) and the RDR note (75th 61)
  // both already ellipsise at typical lengths, so taking width from them moves
  // truncation into the common case rather than the tail.
  client: 150,
  note: 306, // 340 until 2026-08-17; 10% back to pay for the WFM columns (Robert)
  address: 220,
  // 80 fitted the six plain status names; "Re Requested (A)" is the widest thing
  // this column now holds. The bracket rides at the smaller .jl-num size, so it
  // costs less than its character count suggests. Sized for the WIDEST Bootswatch
  // theme (architecture.md §10), like every other number here.
  status: 130,
  // 215 until 2026-08-17. 172 fits "Accepted (31-07-2026)"; the extra was headroom
  // for the re-opened bracket, and on a real 1080p render that was visibly idle
  // space (Robert). 178 keeps the ordinary case comfortable and lets the rare
  // re-opened row ellipsise — it clips, so nothing is lost, and WFM is the ONLY
  // column set using statusSince (the others use `status`), so this is scoped to
  // Workflow Management on its own.
  statusSince: 178,
  division: 88,
  jobType: 120,
  // Measured on dev 2026-08-17, length in characters (median / 90th / max):
  //   insurer   14 / 14 / 37   — one outlier, "Offshore Market Placements Ltd
  //                              (OMPL)"; it ellipsises with the name on hover
  //   assessor  12 / 23 / 23   — "Morgan Project Services" is the widest
  // Sized for the 90th rather than the max, which is the same choice client and
  // address already make: paying 100px for one company nobody scans for costs
  // every other column on every row.
  // Trimmed from 130 each on 2026-08-17 against a 1080p screenshot: at the
  // median (14 and 12 chars) both sat in visibly idle space. Both clip with the
  // full name on hover, so the long tail costs a tooltip rather than a column.
  insurer: 106,
  assessor: 116,
  pco: 150,
  contractor: 180,
  created: 104, // 80 fitted Inter and clipped to "28-07-2…" in the widest theme (Robert 2026-08-03) — a cut-off DATE reads as broken, so this pays for the widest font rather than the default one
  goto: 200,
};
// A job a new inspection has sent backwards: "Re Requested (A)" is a job that
// had reached Accepted and is back at Requested. Plain jobs are unchanged, so
// the column only says something extra when there is something extra to say.
//
// The bracket is a bare code in muted small type, not "(from A)" — Robert, 13
// Aug, "too horrible when viewing lots of records". Down a long list the word
// repeated on every re-opened row was noise; the code alone reads as an
// annotation rather than a sentence. The full name is in the hover.
//
// The CURRENT status leads, and that is the whole point (Robert, 13 Aug, after
// seeing the first version in a real list). The first build put the high-water
// mark first — "Accepted (R)" — and under a Requested filter you got 20 rows
// saying Requested and 3 saying Accepted, which reads as a broken filter. Worse,
// "Accepted" is the most operationally loaded word in this column: on a job that
// is NOT accepted it invites exactly the opposite mistake from the one the
// feature exists to prevent. Leading with the real status keeps the column
// scannable and honest; the bracket carries how far it had got.
//
// "Re Requested" rather than "Re Request" so the column, the search dropdown and
// the Status tab all say one word — getStatusView already names this state.
// The hover behind the Status column: the DATE the job reached the status
// showing, and nothing else (Robert 2026-08-14) — the status is already the word
// you are hovering, so repeating it in the tooltip was noise.
//
// On a re-opened job this is the RE-request date, not the original, which is the
// useful number: how long it has been sitting back where it is.
//
// `undefined` rather than "" when there is no date, so the browser shows no
// tooltip at all instead of an empty box (imported claims can predate the event
// log).
const statusTitle = (claim) =>
  claim.status_since ? formatDate(claim.status_since) : undefined;

const statusLabel = (claim) =>
  claim.reached_beyond_code ? (
    <>
      Re {claim.status_name}
      <span
        className="text-muted ms-1 jl-num"
        data-hint={`Re-opened by a new inspection — this job had reached ${statusName(claim.reached_beyond_code)}`}
      >
        ({claim.reached_beyond_code})
      </span>
    </>
  ) : (
    claim.status_name
  );

// Needs Attention is a FLOOR, not a width — it is the column that takes the
// leftover, so on a wide monitor it grows well past this and the number only
// bites in a narrow window.
const ATTENTION_FLOOR = 340;
// Workflow Management runs a lower one, and gives the 80px to the RDR note
// (Robert 2026-08-03). It can afford to: `badgeGroup` filters these lists to
// workflow rules only, so a row wears one or two chips where an Accounts row
// can wear four.
//
// Dropped 260 -> 135 on 2026-08-17 to pay for Job Type / Insurer / Assessor,
// which Karen asked for in the same breath: "we can make needs attention smaller
// as normally only 2 badges shown", and Robert confirmed on the render that two
// lines of chips is an acceptable trade.
//
// THE BINDING CONSTRAINT IS NO HORIZONTAL SCROLLBAR AT 1080p. This is a FLOOR,
// not a width — the column still takes every spare pixel on a wider monitor — so
// lowering it costs nothing except how early chips wrap in a narrow window, and
// it is the only column that can give without an ellipsis appearing somewhere.
// Trim here first; the three new columns are already at their 90th percentile.
export const WFM_ATTENTION_FLOOR = 135;

// Next-action badges for one row. Compact = the TOP badge (rule order = severity
// until Karen ranks the tiers) + a "+N" overflow whose tooltip lists the rest.
// Extended = every chip. `suppressKey` hides the rule you're already inside
// (RDR Show-me lists). Colour = the rule's report group.
function RowBadges({ claimId, list, extended, suppressKey, primaryKey, groupFilter, onOpen, onFilter }) {
  let shown = (list || []).filter((b) => b.key !== suppressKey);
  // groupFilter (Accounts/H&S/WFM Show-me lists — Karen 2026-07-15): only this
  // category's badges appear; other departments' problems don't, not even as +N.
  if (groupFilter) shown = shown.filter((b) => b.group === groupFilter);
  if (shown.length === 0) return null;
  // primaryKey (RDR Show-me lists): THIS report's badge is the visible one —
  // the rest fold into +N regardless of severity order (Karen 2026-07-15).
  if (primaryKey && shown.some((b) => b.key === primaryKey)) {
    shown = [shown.find((b) => b.key === primaryKey), ...shown.filter((b) => b.key !== primaryKey)];
  }
  // Extended = ALL badges on ONE line, full labels. Every chip is a deep-link
  // to the tab where that issue gets FIXED (rule.tab from the engine).
  const visible = extended ? shown : shown.slice(0, 1);
  const rest = shown.slice(visible.length);
  // No wrapper: the chips render straight into the cell's flex row (.job-badges-cell)
  // so the note ⓘ and the badges share one wrapping line. Extended = every chip
  // (wraps to new lines when many); compact = top badge + a "+N" overflow.
  return (
    <>
      {visible.map((b) => (
        <button
          key={b.key}
          type="button"
          className={`job-badge job-badge-${b.group} job-badge-link ${extended ? "job-badge-slim" : ""}`}
          data-hint={`${b.label} — open the ${b.tab} tab to sort it out`}
          // Shift-select highlights text; suppress it so the power-move below feels clean.
          onMouseDown={(event) => (event.shiftKey || event.ctrlKey) && event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            // Power-user move: Shift+click OR Ctrl+click a badge = filter the whole
            // list to it (sets the Needs Attention dropdown + re-searches). Only where
            // the host page wires onFilter (the search page); elsewhere it deep-links.
            // (Safe to claim Ctrl+click: these are buttons, not <a> — no new-tab default.)
            if ((event.shiftKey || event.ctrlKey) && onFilter) {
              onFilter(b.key);
              return;
            }
            onOpen(claimId, b.tab);
          }}
        >
          {b.label}
        </button>
      ))}
      {rest.length > 0 ? (
        <span className="job-badge job-badge-more" data-hint={rest.map((b) => b.label).join("\n")}>
          +{rest.length}
        </span>
      ) : null}
    </>
  );
}

// The RDR note, edited where it is read (Robert 2026-08-03). The office keeps
// this line current from the WFM list — 1,317 of 5,000 jobs carry one — so
// making them open the job, find the glance and come back is the whole cost of
// the feature.
//
// It WRAPS rather than truncating, the same call Robert made for the Important
// Jobs report on 2026-08-01: the note is the reason the job is on the list, and
// half a sentence is no use. The row simply gets taller.
// A job sits on two worklists at once all the time here, so the same note can be
// on screen twice. Saving one and leaving the other showing the old text is the
// kind of thing that quietly costs a page its credibility.
//
// A module-scoped subscriber list, NOT state lifted up to the report: lifting it
// would put two more props on JobList — a component every list in the app shares
// — to fix something one column has. This stays in the file that owns the
// feature and adds no API surface. Cells unsubscribe on unmount.
const noteWatchers = new Set();

function NoteCell({ claim }) {
  const [value, setValue] = useState(claim.rdr_note || "");
  useEffect(() => {
    // Only the displayed value follows a sibling's save — never an open draft,
    // so nobody's half-typed note disappears under them.
    const watcher = (id, note) => id === claim.id && setValue(note);
    noteWatchers.add(watcher);
    return () => noteWatchers.delete(watcher);
  }, [claim.id]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    setBusy(true);
    setError("");
    const next = draft.trim();
    try {
      await updateClaim(claim.id, { rdr_note: next || null });
      for (const watcher of noteWatchers) watcher(claim.id, next); // includes this one
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save.");
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <>
        <Form.Control
          size="sm"
          as="textarea"
          rows={3}
          value={draft}
          autoFocus
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          // Enter inserts a newline here (notes run to several lines in 1.0), so
          // saving is Ctrl/Cmd+Enter or the button. Escape abandons the edit.
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) save();
            if (e.key === "Escape") setEditing(false);
          }}
        />
        {error ? <div className="small text-danger">{error}</div> : null}
        <div className="d-flex align-items-center gap-1 mt-1">
          <Button size="sm" onClick={save} disabled={busy}>Save</Button>
          <Button size="sm" variant="secondary" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </Button>
          {busy ? <Spinner size="sm" /> : null}
        </div>
      </>
    );
  }
  return (
    <div
      role="button"
      className="jl-note"
      data-hint="Click to edit the RDR note"
      onClick={() => {
        setDraft(value);
        setError("");
        setEditing(true);
      }}
    >
      {value ? value : <span className="text-muted">-</span>}
      <FaPencilAlt className="jl-note-pencil" />
    </div>
  );
}

function JobList({
  rows,
  emptyText = "No claims found.",
  badges = null, // { claim_id: [{key,label,group}] } — omit to hide the badge row
  badgesExtended = false,
  primaryBadgeKey = null, // compact mode: force THIS rule's badge to be the visible chip
  badgeGroup = null, // show ONLY this report group's badges (Accounts/H&S/WFM lists)
  suppressBadgeKey = null,
  onBadgeFilter = null, // (ruleKey) => void — Shift+click a badge filters the list to it
  pcoHeader = "Project Co", // the short header the search page always used — the full "Project Co-Ordinator" wrapped to 2 lines and cost the column width for nothing (Robert 2026-08-03)
  rowTab = null, // the row's click lands on this tab instead of Details (PCO dashboard review worklists open Progress — Karen 2026-07-25)
  sortKey = "", // ARCHIVE: current claim-number direction ("" newest, "oldest")
  onSort = null, // (nextSort) => void — supplied only where the header should sort
  activeFilter = null, // {label, group} — the active Needs Attention filter, shown as a chip in the column header
  onClearFilter = null, // () => void — the chip's × (clears the filter + re-searches)
  aligned = false, // stacked lists (an RDR section) pin the columns so they line up down the page — styles/jobList.css
  columns = DEFAULT_COLUMNS, // which columns, in what order — DEFAULT_COLUMNS / WFM_COLUMNS
  attentionFloor = ATTENTION_FLOOR, // minimum for Needs Attention — WFM_ATTENTION_FLOOR alongside WFM_COLUMNS
}) {
  const navigate = useNavigate();
  const open = (claim, tab) => navigate(`/claims/${claim.claim_number}${tab ? `?tab=${tab}` : ""}`);
  // Claim # and Client open the job; the rest of the row does not (Robert
  // 2026-08-03 — a whole-row target makes an accidental jump out of the report
  // too easy, and cannot survive editing a note in place).
  const openCell = (claim, extraClass) => ({
    className: `jl-open clickable ${extraClass}`,
    onClick: () => open(claim, rowTab),
  });

  // Rule-specific extra column (H&S show-me lists name the offending
  // contractor, as 1.0) — appended only when the rows actually carry one.
  const showContractors = rows.some((r) => r.contractors);
  const shown = showContractors ? [...columns, "contractor"] : columns;

  // Every column in one place: its header, its cell, and the class that clips it
  // when the fixed layout would otherwise let it spill into its neighbour.
  const COLUMNS = {
    claim: {
      // ARCHIVE: the header sorts (Robert 2026-08-21) — clicking flips
      // newest/oldest, which is how you reach job 1000 without paging through
      // twenty years. Only rendered clickable when a caller passes onSort, so
      // every other JobList surface is untouched.
      header: onSort ? (
        <span
          role="button"
          className="user-select-none"
          title={sortKey === "oldest" ? "Sorted oldest first — click for newest" : "Sorted newest first — click for oldest"}
          onClick={() => onSort(sortKey === "oldest" ? "" : "oldest")}
        >
          Claim # <span className="text-muted">{sortKey === "oldest" ? "▲" : "▼"}</span>
        </span>
      ) : (
        "Claim #"
      ),
      clip: true,
      headClass: "text-nowrap",
      cell: (claim) => <td key="claim" {...openCell(claim, "text-nowrap jl-clip")}>{claim.claim_number}</td>,
    },
    client: {
      header: "Client",
      // Client + Address both truncate with an ellipsis + full-text tooltip
      // (the office describes jobs by ADDRESS as much as by name).
      // maxWidth, not just the header width: .text-truncate needs a bound to
      // ellipsise against, and the search page runs UNALIGNED with no th width.
      cell: (claim) => (
        <td
          key="client"
          {...openCell(claim, "text-truncate")}
          style={{ maxWidth: WIDTHS.client }}
          data-hint={claim.client_name || undefined}
        >
          {claim.client_name || "-"}
        </td>
      ),
    },
    note: {
      header: "RDR Note",
      cell: (claim) => <td key="note"><NoteCell claim={claim} /></td>,
    },
    address: {
      header: "Address",
      cell: (claim) => (
        <td key="address" className="text-truncate" style={{ maxWidth: WIDTHS.address }} data-hint={claim.street_address || undefined}>
          {claim.street_address || "-"}
        </td>
      ),
    },
    status: {
      header: "Status",
      clip: true,
      // Hover gives the DATE the job reached the status showing (Robert
      // 2026-08-14). Workflow Management prints the same date in its own Status
      // column; on the search there is no room for it, so it appears in the hint
      // rather than costing every row 60px of width.
      cell: (claim) => (
        <td key="status" className="jl-clip text-nowrap" data-hint={statusTitle(claim)}>
          {statusLabel(claim)}
        </td>
      ),
    },
    statusSince: {
      header: "Status",
      clip: true,
      // 1.0 stacked the date under the status; one column says the same thing
      // and buys back the width (Robert 2026-08-03).
      cell: (claim) => (
        <td key="statusSince" className="jl-clip text-nowrap">
          {statusLabel(claim)}
          {claim.status_since ? (
            <span className="text-muted ms-1 jl-num">({formatDate(claim.status_since)})</span>
          ) : null}
        </td>
      ),
    },
    division: {
      header: "Division",
      clip: true,
      cell: (claim) => <td key="division" className="jl-clip text-nowrap">{claim.division_name || "-"}</td>,
    },
    // Karen Renall and Marissa both raised this independently 2026-08-05:
    // searching an address returns a Callout and an R&Q and nothing says which
    // is which. The data was already on the row, only the column was missing.
    jobType: {
      header: "Job Type",
      clip: true,
      // title because it DOES clip — "Tender Reinstatement" shows as
      // "Tender Reinstate…" and had no way to read the rest (Robert, 17 Aug).
      cell: (claim) => (
        <td key="jobType" className="jl-clip text-nowrap" data-hint={claim.job_type_name || undefined}>
          {claim.job_type_name || "-"}
        </td>
      ),
    },
    // Workflow Management, from 1.0 (Karen, 16 Aug). 1.0 stacks Insurer/Assessor
    // in one cell; here they are columns of their own because rows stay ONE LINE.
    insurer: {
      header: "Insurer",
      clip: true,
      cell: (claim) => (
        <td key="insurer" className="jl-clip text-nowrap" data-hint={claim.insurer_name || undefined}>
          {claim.insurer_name || "-"}
        </td>
      ),
    },
    assessor: {
      header: "Assessor",
      clip: true,
      cell: (claim) => (
        <td key="assessor" className="jl-clip text-nowrap" data-hint={claim.assessor_company_name || undefined}>
          {claim.assessor_company_name || "-"}
        </td>
      ),
    },
    pco: {
      header: pcoHeader,
      clip: true,
      // Hint too: the fixed layout can ellipsise a long name (the longest in
      // use, Mia-Mae Van Der Merwe, fits).
      cell: (claim) => (
        <td key="pco" className="jl-clip text-nowrap" data-hint={claim.project_coordinator_name || undefined}>
          {claim.project_coordinator_name || "-"}
        </td>
      ),
    },
    contractor: {
      header: "Contractor",
      cell: (claim) => (
        <td key="contractor" className="text-truncate" style={{ maxWidth: WIDTHS.contractor }} data-hint={claim.contractors || undefined}>
          {claim.contractors || "-"}
        </td>
      ),
    },
    created: {
      header: "Created",
      clip: true,
      // jl-clip as well as nowrap: nowrap on its own is what let a wide-font
      // theme run this date over the Go to column (Robert 2026-08-03).
      cell: (claim) => <td key="created" className="text-nowrap jl-clip jl-num">{formatDate(claim.created_at)}</td>,
    },
    goto: {
      header: "Go to",
      headClass: "text-end",
      cell: (claim) => (
        <td key="goto" className="text-end text-nowrap">
          <div className="jl-goto-rail" role="group" aria-label="Go to tab">
            {TAB_ICONS.map(({ tab, title, icon: Icon, color }) => (
              <Hint key={tab} label={title}>
                <Button
                  // Files uses the theme's purple; secondary can be white.
                  size="sm"
                  variant={`outline-${color === "purple" ? "secondary" : color}`}
                  className={`jl-goto${color === "purple" ? " jl-goto-files" : ""}`}
                  aria-label={title}
                  onClick={() => open(claim, tab)}
                >
                  <Icon />
                </Button>
              </Hint>
            ))}
          </div>
        </td>
      ),
    },
    attention: {
      header: (
        <>
          Needs Attention
          {/* The active filter as a dismissible chip — the header has the room
              the filter bar doesn't. Click = clear the filter, full list back. */}
          {activeFilter ? (
            <button
              type="button"
              className={`job-badge job-badge-link job-badge-${activeFilter.group} ms-2 fw-semibold`}
              data-hint={`Showing only jobs with "${activeFilter.label}" — click to clear`}
              aria-label={`Clear Needs Attention filter: ${activeFilter.label}`}
              onClick={onClearFilter}
            >
              {activeFilter.label} <span aria-hidden="true">×</span>
            </button>
          ) : null}
        </>
      ),
      headClass: "needs-attention-cell",
      cell: (claim) => (
        <td key="attention" className="needs-attention-cell">
          {/* One wrapping flex row: the note ⓘ (hover for the note) + every
              badge share a line, spilling onto new lines only when too many. */}
          <div className="job-badges-cell">
            {badges ? (
              <RowBadges
                claimId={claim.id}
                list={badges[claim.id]}
                extended={badgesExtended}
                primaryKey={primaryBadgeKey}
                groupFilter={badgeGroup}
                suppressKey={suppressBadgeKey}
                onOpen={(_id, tab) => navigate(`/claims/${claim.claim_number}?tab=${tab}`)}
                onFilter={onBadgeFilter}
              />
            ) : null}
            {!(badges && (badges[claim.id] || []).length) ? (
              <span className="text-muted">-</span>
            ) : null}
          </div>
        </td>
      ),
    },
  };

  // The table is laid out at the sum of the columns it is actually showing, and
  // the .table-responsive wrapper scrolls when the window is narrower — which is
  // what stops the fixed layout from squeezing everything into the viewport.
  const minWidth = shown.reduce((sum, key) => sum + (WIDTHS[key] || 0), attentionFloor);

  return (
    <Table
      striped bordered hover responsive size="sm"
      className={`mb-0 align-middle${aligned ? " job-list-aligned" : ""}`}
      style={aligned ? { minWidth } : undefined}
    >
      <thead className="table-surface">
        <tr>
          {shown.map((key) => {
            const col = COLUMNS[key];
            return (
              <th
                key={key}
                className={`${col.headClass || ""}${col.clip ? " jl-clip" : ""}`}
                style={aligned && WIDTHS[key] ? { width: WIDTHS[key] } : undefined}
              >
                {col.header}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((claim) => (
          <tr key={claim.id}>{shown.map((key) => COLUMNS[key].cell(claim))}</tr>
        ))}
        {rows.length === 0 ? (
          <tr>
            <td colSpan={shown.length} className="text-center text-muted">{emptyText}</td>
          </tr>
        ) : null}
      </tbody>
    </Table>
  );
}

export default JobList;
